import React, {
  useEffect, useState, useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Spinner } from '@blueprintjs/core';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { Ref } from 'routes/RadiusParserOptions/components/RadiusParserOptionsForm/RadiusParserOptionsForm';
import RadiusParserOptionsForm from 'routes/RadiusParserOptions/components/RadiusParserOptionsForm';
import { RootState } from 'data/root.reducers';
import * as radiusParserOptionsSelectors from 'data/radiusParserOptions/radiusParserOptions.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as radiusParserOptionsActions from 'data/radiusParserOptions/radiusParserOptions.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { RadiusParserOptions } from 'data/radiusParserOptions/radiusParserOptions.types';
import { radiusParserOptionsTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
} from '../../utils/radiusParserOptionsTemplate.adapter';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  radiusParserOptions: radiusParserOptionsSelectors.getRadiusParserOptions(state),
  radiusParserOptionsAdaptedTemplate: adaptTemplateVisibilityMode(radiusParserOptionsTemplate, 'view'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  isRadiusParserOptionsLoading: radiusParserOptionsSelectors.isRadiusParserOptionsLoading(state),
});

const mapDispatch = {
  goToRadiusParserOptionsEdit: radiusParserOptionsActions.goToRadiusParserOptionsEdit,
  goToRadiusParserOptionsCreate: radiusParserOptionsActions.goToRadiusParserOptionsCreate,
  getRadiusParserOptions: radiusParserOptionsActions.getRadiusParserOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const RadiusParserOptionsView = ({
  history,
  radiusParserOptions,
  radiusParserOptionsAdaptedTemplate,
  isRadiusParserOptionsLoading,
  getRadiusParserOptions,
  goToRadiusParserOptionsEdit,
  goToRadiusParserOptionsCreate,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<RadiusParserOptions>>({});
  const [template, setTemplate] = useState(radiusParserOptionsAdaptedTemplate);
  const { t } = useTranslation();

  useEffect(() => {
    getRadiusParserOptions();
  }, [getRadiusParserOptions]);

  useEffect(() => {

    if (radiusParserOptionsAdaptedTemplate == null) {
      return;
    }

    if (radiusParserOptions != null) {
      setFormData(radiusParserOptions);
    }

    const clonedTemplate = cloneDeep(radiusParserOptionsAdaptedTemplate) as FormTemplate;
    clonedTemplate.formId = Date.now().toString();
    setTemplate(clonedTemplate);

  }, [radiusParserOptions, radiusParserOptionsAdaptedTemplate]);

  const handleClickEdit = () => {
    if (radiusParserOptions?.id != null) {
      goToRadiusParserOptionsEdit(history);
    } else {
      goToRadiusParserOptionsCreate(history);
    }
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as RadiusParserOptions);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <RadiusParserOptionsForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing={false}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel>
        <FormButtons
          isEditing={false}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  if (isRadiusParserOptionsLoading) {
    return <Spinner aria-label="Loading..." />;
  }

  if (!radiusParserOptions?.id) {
    return <div />;
  }

  return (
    <div data-testid="RadiusParserOptionsView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(RadiusParserOptionsView));
